/**
 * @fileoverview React Query hooks for SMS messaging functionality.
 * Provides data fetching and mutations for conversation threads,
 * opt-out status, SMS settings, and sending messages.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import type { SMSConversation, ConversationThread, SMSOptOut } from '@/types/message';

/**
 * Formats a phone number for display.
 * @param phone - Raw phone number string
 * @returns Formatted phone number (e.g., "+1 (555) 123-4567")
 */
export function formatPhoneNumber(phone: string): string {
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  return phone;
}

/**
 * Fetches all SMS conversations grouped by phone number.
 * Returns conversation threads sorted by most recent message.
 * @returns Query result with ConversationThread array
 */
export function useConversations() {
  const { activeTenantId } = useAuth();

  return useQuery({
    queryKey: ['conversations', activeTenantId],
    queryFn: async (): Promise<ConversationThread[]> => {
      if (!activeTenantId) return [];

      const startTime = performance.now();
      const { data, error } = await supabase
        .from('calie_sms_conversations')
        .select('*')
        .eq('tenant_id', activeTenantId)
        .order('sent_at', { ascending: false });

      if (error) {
        console.error('[useConversations] Failed to fetch:', error.message);
        throw error;
      }
      
      console.debug(`[useConversations] Fetched ${data?.length || 0} messages in ${Math.round(performance.now() - startTime)}ms`);

      // Group messages by phone number
      const grouped = new Map<string, SMSConversation[]>();
      (data || []).forEach((msg) => {
        const existing = grouped.get(msg.phone_number) || [];
        existing.push(msg as SMSConversation);
        grouped.set(msg.phone_number, existing);
      });

      // Convert to threads
      const threads: ConversationThread[] = [];
      grouped.forEach((messages, phone_number) => {
        const sortedMessages = messages.sort(
          (a, b) => new Date(b.sent_at).getTime() - new Date(a.sent_at).getTime()
        );
        const lastMessage = sortedMessages[0];
        const hasInbound = messages.some((m) => m.message_direction === 'inbound');

        threads.push({
          phone_number,
          messages: sortedMessages,
          last_message_at: lastMessage.sent_at,
          last_message_preview: lastMessage.message_body.slice(0, 100),
          unread_count: 0, // TODO: Implement read status tracking
          has_inbound: hasInbound,
          linked_call_id: messages.find((m) => m.call_id)?.call_id || null,
        });
      });

      // Sort threads by last message
      return threads.sort(
        (a, b) => new Date(b.last_message_at).getTime() - new Date(a.last_message_at).getTime()
      );
    },
    enabled: !!activeTenantId,
  });
}

/**
 * Fetches all messages for a specific phone number conversation.
 * @param phoneNumber - The phone number to fetch messages for
 * @returns Query result with SMSConversation array sorted by sent_at
 */
export function useConversationThread(phoneNumber: string | null) {
  const { activeTenantId } = useAuth();

  return useQuery({
    queryKey: ['conversation-thread', activeTenantId, phoneNumber],
    queryFn: async (): Promise<SMSConversation[]> => {
      if (!activeTenantId || !phoneNumber) return [];

      const startTime = performance.now();
      const { data, error } = await supabase
        .from('calie_sms_conversations')
        .select('*')
        .eq('tenant_id', activeTenantId)
        .eq('phone_number', phoneNumber)
        .order('sent_at', { ascending: true });

      if (error) {
        console.error('[useConversationThread] Failed to fetch:', error.message);
        throw error;
      }
      
      console.debug(`[useConversationThread] Fetched ${data?.length || 0} messages for ${phoneNumber} in ${Math.round(performance.now() - startTime)}ms`);
      return (data || []) as SMSConversation[];
    },
    enabled: !!activeTenantId && !!phoneNumber,
  });
}

/**
 * Checks if a phone number has opted out of SMS communications.
 * @param phoneNumber - The phone number to check
 * @returns Query result with SMSOptOut record or null
 */
export function useOptOutStatus(phoneNumber: string | null) {
  const { activeTenantId } = useAuth();

  return useQuery({
    queryKey: ['opt-out-status', activeTenantId, phoneNumber],
    queryFn: async (): Promise<SMSOptOut | null> => {
      if (!activeTenantId || !phoneNumber) return null;

      const { data, error } = await supabase
        .from('calie_sms_optouts')
        .select('*')
        .eq('tenant_id', activeTenantId)
        .eq('phone_number', phoneNumber)
        .maybeSingle();

      if (error) {
        console.error('[useOptOutStatus] Failed to check opt-out:', error.message);
        throw error;
      }
      
      if (data) {
        console.debug(`[useOptOutStatus] ${phoneNumber} is opted out`);
      }
      return data as SMSOptOut | null;
    },
    enabled: !!activeTenantId && !!phoneNumber,
  });
}

/**
 * Fetches tenant SMS settings including auto-reply templates and rate limits.
 * @returns Query result with SMS settings or null if not found
 */
export function useSMSSettings() {
  const { activeTenantId } = useAuth();

  return useQuery({
    queryKey: ['sms-settings', activeTenantId],
    queryFn: async () => {
      if (!activeTenantId) return null;

      const { data, error } = await supabase
        .from('calie_tenants')
        .select(
          'sms_auto_reply_enabled, sms_auto_reply_template, missed_call_sms_template, sms_rate_limit_per_hour, sms_rate_limit_per_day, booking_url'
        )
        .eq('tenant_id', activeTenantId)
        .maybeSingle();

      if (error) {
        console.error('[useSMSSettings] Failed to fetch SMS settings:', error.message);
        throw error;
      }
      
      return data;
    },
    enabled: !!activeTenantId,
  });
}

/**
 * Mutation hook to send an SMS message via Calie_sendSMS edge function.
 * @returns Mutation object for sending SMS
 */
export function useSendSMS() {
  const queryClient = useQueryClient();
  const { activeTenantId } = useAuth();

  return useMutation({
    mutationFn: async ({
      toNumber,
      messageBody,
      callId,
      fromNumber,
    }: {
      toNumber: string;
      messageBody: string;
      callId?: string;
      fromNumber?: string;
    }) => {
      if (!activeTenantId) {
        throw new Error('No active tenant');
      }

      console.debug('[useSendSMS] Sending SMS to:', toNumber);

      const { data, error } = await supabase.functions.invoke('Calie_sendSMS', {
        body: {
          toNumber,
          messageBody,
          callId,
          fromNumber,
          tenantId: activeTenantId,
        },
      });

      if (error) {
        console.error('[useSendSMS] Edge function error:', error);
        throw new Error(error.message || 'Failed to send SMS');
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Failed to send SMS');
      }

      console.debug('[useSendSMS] SMS sent successfully:', data.messageSid);
      return data;
    },
    onSuccess: (_, variables) => {
      // Invalidate both conversation list and thread
      queryClient.invalidateQueries({ queryKey: ['conversations', activeTenantId] });
      queryClient.invalidateQueries({ queryKey: ['conversation-thread', activeTenantId, variables.toNumber] });
    },
  });
}
